import React, { useEffect, useMemo } from 'react';
import { Box, Grid, MenuItem, TextField, Theme, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { useFormikContext } from 'formik';
import { MultiSelect } from '@campfire/multi-select';
import { useCampfireTheme } from '../../../../../../../../theme/useCampfireTheme';
import { useCampfireQuery } from '../../../../../../../../global/network/useCampfireQuery';
import { GET_PWI_WIDGET_OPTIONS } from '../../../widgets/pwi-widget/pwi-widget-options.gql';
import {
  GetPwiWidgetOptions,
  GetPwiWidgetOptions_vm_tasks_taskItems_VOLUNTEER_TaskItemFieldType as TaskItemField,
} from '../../../widgets/pwi-widget/__generated__/GetPwiWidgetOptions';
import { CreatWidgetFormValues, PwiFormValues, PwiItemRole } from '../../dashboard-widget-model';
import { useApplyCustomWords } from '../../../useCustomWords';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    title: {
      color: theme.color.grey.neutral500,
      fontWeight: 700,
      fontSize: '18px',
      marginBottom: '8px',
    },
    label: {
      fontSize: '14px',
      fontWeight: 700,
      color: theme.color.grey.neutral500,
      paddingBottom: '6px',
    },
    helper: {
      fontSize: '13px',
      color: theme.color.grey.neutral300,
    },
    question: {
      gap: 12,
      padding: '4px 0',
      borderBottom: `1px solid ${theme.color.grey.neutralBrand200}`,
    },
  })
);

type Question = { key: string; fieldId: string; rowId: string | null; label: string };

const SLIDER_TYPE = 'VOLUNTEER_LinearScaleTaskFieldType';
const MATRIX_TYPE = 'VOLUNTEER_MatrixTaskFieldType';
const SEPARATE_BY_DEFAULT = /(life as a whole|spirituality)/i;

export const pwiItemKey = (fieldId: string, rowId: string | null) => `${fieldId}:${rowId ?? ''}`;

export const EMPTY_PWI: PwiFormValues = { name: '', programIds: [], taskId: null, roles: {} };

export const pwiFormFromFilters = (name: string, filters: string): PwiFormValues => {
  try {
    const parsed = JSON.parse(filters);
    const roles: Record<string, PwiItemRole> = {};
    (parsed.items ?? []).forEach((item: { fieldId: string; rowId: string | null; role: PwiItemRole }) => {
      roles[pwiItemKey(item.fieldId, item.rowId)] = item.role;
    });
    return { name, programIds: parsed.programIds ?? [], taskId: parsed.taskId ?? null, roles };
  } catch (e) {
    return { ...EMPTY_PWI, name };
  }
};

export const pwiItemsFromForm = (pwi: PwiFormValues) =>
  Object.entries(pwi.roles)
    .filter(([, role]) => role !== 'ignore')
    .map(([key, role]) => {
      const [fieldId, rowId] = key.split(':');
      return { fieldId, rowId: rowId || null, role };
    });

export function PwiWidgetForm() {
  const { values, setFieldValue } = useFormikContext<CreatWidgetFormValues>();
  const cw = useApplyCustomWords();
  const classes = useStyles();
  const { theme } = useCampfireTheme();
  const inputStyle = { borderRadius: '8px', color: theme.color.grey.neutral300 };
  const pwi = values.pwi ?? EMPTY_PWI;
  const { data } = useCampfireQuery<GetPwiWidgetOptions, {}>(GET_PWI_WIDGET_OPTIONS);

  const tasks = data?.vm.tasks ?? [];
  const programs = data?.vm.programs ?? [];
  const task = tasks.find((option) => option.taskId === pwi.taskId);

  const update = (patch: Partial<PwiFormValues>) => setFieldValue('pwi', { ...pwi, ...patch });

  const questions: Question[] = useMemo(() => {
    if (!task) return [];
    const found: Question[] = [];
    task.taskItems.forEach((item) => {
      if (item.__typename !== 'VOLUNTEER_TaskItemFieldType') return;
      const { field } = item as TaskItemField;
      if (field.__typename === SLIDER_TYPE) {
        found.push({
          key: pwiItemKey(field.taskFieldId, null),
          fieldId: field.taskFieldId,
          rowId: null,
          label: field.name,
        });
      }
      if (field.__typename === MATRIX_TYPE) {
        (field.rows ?? []).forEach((row) => {
          found.push({
            key: pwiItemKey(field.taskFieldId, row.matrixRowId),
            fieldId: field.taskFieldId,
            rowId: row.matrixRowId,
            label: `${field.name}: ${row.name}`,
          });
        });
      }
    });
    return found;
  }, [task]);

  useEffect(() => {
    if (questions.length === 0) return;
    const roles: Record<string, PwiItemRole> = {};
    questions.forEach((question) => {
      roles[question.key] =
        pwi.roles[question.key] ?? (SEPARATE_BY_DEFAULT.test(question.label) ? 'separate' : 'index');
    });
    update({ roles });
  }, [questions]);

  return (
    <Grid container direction='column' style={{ width: '100%', marginBottom: '16px' }}>
      <Grid item xs={12}>
        <Typography className={classes.title}>Filtering</Typography>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <Typography className={classes.label}>Name</Typography>
            <TextField
              id='pwi-widget-name'
              fullWidth
              placeholder='e.g. Adult PWI'
              variant='outlined'
              value={pwi.name}
              onChange={(event) => update({ name: event.target.value })}
              InputProps={{ style: inputStyle }}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography className={classes.label}>Wellbeing task</Typography>
            <TextField
              id='pwi-widget-task'
              select
              fullWidth
              variant='outlined'
              value={pwi.taskId ?? ''}
              onChange={(event) => update({ taskId: event.target.value || null, roles: {} })}
              SelectProps={{ displayEmpty: true }}
              InputProps={{ style: inputStyle }}
            >
              <MenuItem value=''>Choose a task</MenuItem>
              {tasks.map((option) => (
                <MenuItem key={option.taskId} value={option.taskId}>
                  {option.title}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <Typography className={classes.label}>{cw('Programs')}</Typography>
            <MultiSelect
              variant='outlined'
              name='pwi.programIds'
              placeholder='Everyone who completed the task'
              items={programs.map((program) => ({ value: program.programId, label: program.name }))}
              style={{ borderRadius: '8px' }}
            />
          </Grid>
        </Grid>
      </Grid>
      {task && (
        <Grid item xs={12} style={{ marginTop: '24px' }}>
          <Typography className={classes.label}>Questions</Typography>
          {questions.length === 0 && (
            <Typography className={classes.helper}>This task has no slider or matrix questions.</Typography>
          )}
          {questions.map((question) => (
            <Box
              key={question.key}
              display='flex'
              alignItems='center'
              justifyContent='space-between'
              className={classes.question}
            >
              <Typography style={{ fontSize: '14px', minWidth: 0, color: theme.color.grey.neutral500 }}>
                {question.label}
              </Typography>
              <TextField
                select
                variant='outlined'
                size='small'
                value={pwi.roles[question.key] ?? 'index'}
                onChange={(event) =>
                  update({ roles: { ...pwi.roles, [question.key]: event.target.value as PwiItemRole } })
                }
                InputProps={{ style: { ...inputStyle, fontSize: '13px', minWidth: 170 } }}
              >
                <MenuItem value='index'>In the index</MenuItem>
                <MenuItem value='separate'>Shown separately</MenuItem>
                <MenuItem value='ignore'>Ignore</MenuItem>
              </TextField>
            </Box>
          ))}
        </Grid>
      )}
      <Grid item xs={12} style={{ marginTop: '16px' }}>
        <Typography className={classes.helper}>
          Each participant&apos;s 1st, 2nd and 3rd approved completion of the task count as Start, 6M and Closure.
        </Typography>
      </Grid>
    </Grid>
  );
}
